import { Dialog } from "./dialog";
import { Button } from "../buttons/button";
import { SimpleButton } from "../buttons/simple-button";
import { EvolutionOptionInfo } from "types/web-app";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { useState } from "react";
import { StandardFrame } from "../frames/standard-frame";

interface EvolutionSelectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (targetSpecies: string) => void;
  pokemonName: string;
  pokemonBaseName: string;
  evolutions: EvolutionOptionInfo[];
  isLoading?: boolean;
}

/**
 * Format Pokemon/item name for display
 */
const formatName = (name: string): string => {
  return name
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const EvolutionSelectDialog = ({
  isOpen,
  onClose,
  onSelect,
  pokemonName,
  pokemonBaseName,
  evolutions,
  isLoading,
}: EvolutionSelectDialogProps) => {
  const [selectedEvolution, setSelectedEvolution] = useState<string | null>(
    null,
  );

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (selectedEvolution) {
      onSelect(selectedEvolution);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <div className="px-2 mx-auto ">
        <StandardFrame
          variant="blue"
          className="min-w-[280px] xs:min-w-[320px]  max-w-lg w-full "
        >
          <div className="text-center mt-4">
            <h2 className="text-2xl font-bold text-pk-white mb-2">
              EVOLVE {formatName(pokemonName).toUpperCase()}
            </h2>
            <p className="text-pk-gray-4 mb-4 text-sm">
              Choose a target species
            </p>

            {evolutions.length === 0 && (
              <div className="text-pk-gray-4 my-8 text-xs">
                No evolution options available for this species.
              </div>
            )}

            {/* Evolution Options */}
            <div className="grid grid-cols-1 gap-3 mb-6 max-h-[300px] overflow-y-auto mx-1 mt-8 pt-4 -translate-y-4">
              {evolutions.map((evo) => (
                <button
                  key={evo.target_species}
                  onClick={() => setSelectedEvolution(evo.target_species)}
                  disabled={isLoading}
                  className={`
                  flex items-center gap-3 p-3 rounded-lg border-2 transition-all
                  ${
                    selectedEvolution === evo.target_species
                      ? "border-pk-green-4 bg-pk-green-4/20"
                      : "border-pk-gray-4 bg-pk-gray-3/50 hover:border-pk-garnet-3"
                  }
                  ${
                    isLoading
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }
                `}
                >
                  {/* Pokemon Sprite Preview */}
                  <div className="w-16 h-16 flex items-center justify-center bg-pk-gray-2 rounded-lg overflow-visible">
                    <PokemonSprite
                      pokemon={{
                        base_pokemon_name: evo.target_species,
                      }}
                      style={{ scale: 3 }}
                      className=""
                    />
                  </div>

                  {/* Evolution Info */}
                  <div className="flex-1 text-left ml-8">
                    <div className="font-bold text-pk-white text-lg">
                      {formatName(evo.target_species)}
                    </div>

                    {evo.required_item && (
                      <div className="text-sm text-pk-orange-5">
                        Requires: {formatName(evo.required_item)}
                      </div>
                    )}
                    {evo.required_level && (
                      <div className="text-sm text-pk-blue-6">
                        Level {evo.required_level}+
                      </div>
                    )}
                  </div>

                  {/* Selection Indicator */}
                  <div
                    className={`w-8 h-8 rounded-sm border-2 flex items-center justify-center ${
                      selectedEvolution === evo.target_species
                        ? "border-pk-green-4 bg-pk-green-4"
                        : "border-pk-gray-4"
                    }`}
                  >
                    {selectedEvolution === evo.target_species && (
                      <span className="text-pk-black font-bold">✓</span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Buttons */}
            <div className="flex gap-4 items-center justify-center w-full">
              <Button
                variant="gray"
                onClick={onClose}
                disabled={isLoading ?? false}
              >
                Cancel
              </Button>
              {evolutions.length > 0 && (
                <Button
                  variant="green"
                  onClick={handleConfirm}
                  disabled={!selectedEvolution}
                  loading={isLoading}
                  className="grow"
                >
                  Evolve
                </Button>
              )}
            </div>
          </div>
        </StandardFrame>
      </div>
    </Dialog>
  );
};
